<script lang="ts">
  import { createEventDispatcher } from 'svelte'
  import { fade, slide } from 'svelte/transition'
  import { cubicOut } from 'svelte/easing'
  import { settingsStore, settingsActions } from '$lib/stores/settings'
  import { aiServiceActions, selectedModel, aiServiceState } from '$lib/stores/aiService'
  import { getModelById, filterModelsByApiKeys, type AIModel } from '$lib/config/aiModels'
  import { subscriptionStore, openUpgradePrompt, showUpgradePrompt } from '$lib/stores/subscription'
  import { get } from 'svelte/store'
  import { useClickOutside, useCustomEvent } from '$lib/hooks/useEventListener'
  import { errorService } from '$lib/services/ErrorService'
  import { ChevronDown, Check, CircleIcon } from 'lucide-svelte'
  
  // Import provider icons
  import bareunIcon from '../assets/bareun-big.png'
  import naraSpellerIcon from '../assets/nara-speller-icon.svg'
  import openaiIcon from '../assets/ai-model-icons/openai.png'
  import claudeIcon from '../assets/ai-model-icons/claude.png'
  import openrouterIcon from '../assets/ai-model-icons/openrouter.png'
  import googleIcon from '../assets/ai-model-icons/google.png'
  import upstageIcon from '../assets/ai-model-icons/upstage.png'
  import defaultAIIcon from '../assets/han-icon-1024.png'
  
  interface Props {
    onCheck: () => void
    onAIAnalysis?: () => void
    disabled?: boolean
    isChecking?: boolean
    editorText?: string
  }
  
  let { 
    onCheck, 
    onAIAnalysis,
    disabled = false,
    isChecking = false,
    editorText = ''
  }: Props = $props()
  
  const dispatch = createEventDispatcher<{
    'provider-changed': { provider: string }
    'model-changed': { model: string; provider: string }
  }>()
  
  // Component state
  let showDropdown = $state(false)
  let containerRef = $state<HTMLDivElement | null>(null)
  let forceEnabled = $state(false)
  
  // Provider types
  type GrammarProviderType = 'proxy' | 'bareun' | 'bareun-custom' | 'naraSpeller'
  
  // Settings - force reactivity with state
  let settingsState = $state($settingsStore)

  // Subscribe to settings changes to ensure reactivity
  $effect(() => {
    const unsubscribe = settingsStore.subscribe(newSettings => {
      if (newSettings) {
        settingsState = newSettings
      }
    })
    return unsubscribe
  })

  const settings = $derived(settingsState)

  // Check modes - stored in settings
  const grammarCheckEnabled = $derived(settings.unifiedCheck?.grammarEnabled ?? true)
  const aiAnalysisEnabled = $derived(settings.unifiedCheck?.aiEnabled ?? true)
  
  // Available grammar providers
  const availableGrammarProviders = $derived(() => {
    const providers: Array<{
      id: GrammarProviderType
      name: string
      icon?: string
      description: string
      requiresApiKey?: boolean
      isPremium?: boolean
      isCustom?: boolean
    }> = []
    
    const useProxy = import.meta.env.VITE_USE_PROXY !== 'false'
    if (useProxy) {
      providers.push({
        id: 'proxy',
        name: 'Bareun.ai',
        icon: bareunIcon,
        description: '사용 횟수 제한이 있는 무료 서버',
        isPremium: false
      })
    }
    
    if (!useProxy) {
      const subscriptionInfo = get(subscriptionStore)
      if (subscriptionInfo.type !== 'free' && !settings.api?.customServer?.enabled) {
        providers.push({
          id: 'bareun',
          name: 'Bareun.ai 프리미엄',
          icon: bareunIcon,
          description: '무제한 사용 가능한 프리미엄 서버',
          isPremium: true
        })
      }
    }
    
    if (settings.api?.customServer?.providerType === 'naraSpeller' && settings.api?.customServer?.apiKey) {
      providers.push({
        id: 'naraSpeller',
        name: '부산대 맞춤법 검사기',
        icon: naraSpellerIcon,
        description: '부산대학교 한국어 맞춤법 검사',
        requiresApiKey: true
      })
    }
    
    if (settings.api?.customServer?.providerType === 'bareun' && 
        settings.api?.customServer?.serverUrl && 
        settings.api?.customServer?.apiKey) {
      providers.push({
        id: 'bareun-custom',
        name: 'Bareun.ai (사용자 정의)',
        icon: bareunIcon,
        description: '사용자 정의 Bareun 서버',
        requiresApiKey: true,
        isCustom: true
      })
    }
    
    return providers
  })
  
  // Current grammar provider
  const currentGrammarProvider = $derived(() => {
    const providers = availableGrammarProviders()
    
    if (settings.api?.customServer?.enabled && settings.api.customServer.providerType) {
      const providerType = settings.api.customServer.providerType
      if (providerType === 'naraSpeller') {
        return providers.find(p => p.id === 'naraSpeller')
      } else if (providerType === 'bareun') {
        return providers.find(p => p.id === 'bareun-custom')
      }
    }
    
    const useProxy = import.meta.env.VITE_USE_PROXY !== 'false'
    if (!useProxy) {
      const premium = providers.find(p => p.id === 'bareun')
      if (premium) return premium
    }
    
    return providers.find(p => p.id === 'proxy') || providers[0]
  })
  
  // Available AI models
  const availableAIModels = $derived(() => {
    const aiProviders = settings.aiProviders || []
    const enabledProviders = aiProviders
      .filter(p => p.enabled && (
        (p.apiKey && p.apiKey.trim() !== '') || 
        p.id === 'openrouter'
      ))
      .map(p => p.id)
    
    return filterModelsByApiKeys(enabledProviders)
  })
  
  // Current AI model for grammar check (separate from general AI model)
  const currentModelId = $derived.by(() => {
    const unifiedModel = settings.unifiedCheck?.aiModel
    const hasUnifiedModel = unifiedModel && availableAIModels().some(m => m.id === unifiedModel)

    // Use grammar check specific model if set
    if (hasUnifiedModel) {
      return unifiedModel
    }

    // Otherwise fall back to the general selected model
    if ($selectedModel && availableAIModels().some(m => m.id === $selectedModel)) {
      return $selectedModel
    }

    // Default to OpenRouter if available
    return availableAIModels().find(m => m.provider === 'openrouter')?.id ||
           (availableAIModels().length > 0 ? availableAIModels()[0].id : '')
  })
  
  // Sort AI models - current and recommended first
  const sortedAIModels = $derived(() => {
    const models = availableAIModels()
    const current = currentModelId
    
    return models.sort((a, b) => {
      // Current model always first
      if (a.id === current) return -1
      if (b.id === current) return 1
      
      // Recommended models second
      if (a.recommended && !b.recommended) return -1
      if (!a.recommended && b.recommended) return 1
      
      // Then alphabetically
      return a.name.localeCompare(b.name)
    })
  })
  
  const currentAIModel = $derived(getModelById(currentModelId) || null)
  const isAnalyzing = $derived($aiServiceState.isAnalyzing)
  const isUpgradeModalOpen = $derived($showUpgradePrompt)

  // Button states - with force enable override
  const isProcessing = $derived(forceEnabled ? false : (isChecking || isAnalyzing))
  const canProcess = $derived(forceEnabled ? (editorText.trim().length > 0) : (!disabled && !isProcessing && editorText.trim().length > 0))

  const hasAnyProviderEnabled = $derived(grammarCheckEnabled || aiAnalysisEnabled)
  
  // Get button label based on enabled modes
  const buttonLabel = $derived(
    isProcessing ? (
      isChecking && isAnalyzing ? '처리 중...' :
      isChecking ? '검사 중...' :
      isAnalyzing ? '분석 중...' :
      '처리 중...'
    ) : (
      grammarCheckEnabled && aiAnalysisEnabled ? '맞춤법 검사 with AI' :
      grammarCheckEnabled ? '맞춤법 검사' :
      aiAnalysisEnabled ? 'AI 분석' :
      '검사'
    )
  )
  
  // Combined icon display - shows active providers
  const activeProviderIcons = $derived(() => {
    const icons: { src: string; alt: string; type: 'grammar' | 'ai' }[] = []
    
    if (grammarCheckEnabled && currentGrammarProvider()?.icon) {
      icons.push({
        src: currentGrammarProvider()!.icon,
        alt: currentGrammarProvider()!.name,
        type: 'grammar'
      })
    }
    
    if (aiAnalysisEnabled && currentAIModel) {
      // Use AI model icon or a generic AI icon
      const aiIcon = currentAIModel.icon || getProviderIcon(currentAIModel.provider)
      if (aiIcon) {
        icons.push({
          src: aiIcon,
          alt: currentAIModel.name,
          type: 'ai'
        })
      }
    }
    
    return icons
  })
  
  function getProviderIcon(provider: string): string {
    // Return provider-specific icons
    const providerIcons: Record<string, string> = {
      'openai': openaiIcon,
      'anthropic': claudeIcon,
      'openrouter': openrouterIcon,
      'google': googleIcon,
      'upstage': upstageIcon
    }
    return providerIcons[provider] || defaultAIIcon
  }
  
  function toggleDropdown() {
    if (isProcessing) return
    showDropdown = !showDropdown
  }
  
  function closeDropdown() {
    showDropdown = false
  }
  
  useClickOutside(() => containerRef, closeDropdown, () => showDropdown)

  // Listen for reset event to force clear analyzing state
  useCustomEvent('reset-grammar-check-state', () => {
    // Force enable the button
    forceEnabled = true

    // Force reset AI analyzing state to re-enable button
    aiServiceState.update(state => ({
      ...state,
      isAnalyzing: false
    }))

    // Reset force enable after a short delay to return to normal state
    setTimeout(() => {
      forceEnabled = false
    }, 200)
  })
  
  function toggleGrammarCheck() {
    settingsActions.updateUnifiedCheck({ grammarEnabled: !grammarCheckEnabled })
  }
  
  function toggleAIAnalysis() {
    settingsActions.updateUnifiedCheck({ aiEnabled: !aiAnalysisEnabled })
  }
  
  async function selectGrammarProvider(providerId: GrammarProviderType) {
    // Similar logic to original GrammarCheckSelector
    if (providerId === 'bareun') {
      const subscriptionInfo = get(subscriptionStore)
      if (subscriptionInfo.type === 'free') {
        openUpgradePrompt('feature_locked', 'Bareun.ai 프리미엄 맞춤법 검사')
        return
      }
    }
    
    // Update settings based on selected provider
    if (providerId === 'proxy' || providerId === 'bareun') {
      if (settings.api?.customServer?.enabled) {
        settingsActions.updateApiSettings({ 
          customServer: { 
            ...settings.api.customServer, 
            enabled: false
          } 
        })
      }
    } else if (providerId === 'bareun-custom' || providerId === 'naraSpeller') {
      const providerType = providerId === 'bareun-custom' ? 'bareun' : 'naraSpeller'
      settingsActions.updateApiSettings({ 
        customServer: { 
          ...settings.api.customServer, 
          enabled: true,
          providerType
        } 
      })
    }
    
    dispatch('provider-changed', { provider: providerId })
  }
  
  function selectAIModel(model: AIModel) {
    // Save grammar check specific AI model
    settingsActions.updateUnifiedCheck({ aiModel: model.id })
    // Don't update the global AI model selection
    // await aiServiceActions.selectModel(model.id, model.provider)
    dispatch('model-changed', { model: model.id, provider: model.provider })
    // Close dropdown after selection
    closeDropdown()
  }
  
  async function handleProcess() {
    if (!canProcess) return
    
    if (!hasAnyProviderEnabled) {
      errorService.addError('검사 또는 분석 기능을 활성화해주세요', {
        severity: 'warning',
        duration: 3000
      })
      return
    }
    
    // Check both permissions first
    if (grammarCheckEnabled) {
      const canCheck = await subscriptionStore.canPerformAction('spellCheck')
      if (!canCheck) {
        openUpgradePrompt('limit_reached', '맞춤법 검사 일일 한도 도달')
        return
      }
    }
    
    if (aiAnalysisEnabled && onAIAnalysis) {
      const canAnalyze = await subscriptionStore.canPerformAction('aiAnalysis')
      if (!canAnalyze) {
        openUpgradePrompt('limit_reached', 'AI 분석 일일 한도 도달')
        return
      }
    }
    
    // Dispatch unified check event to MainEditor
    // MainEditor will handle the sequential execution
    window.dispatchEvent(new CustomEvent('shortcut:unified-check'))
  }
</script>

<div class="unified-check-container" bind:this={containerRef}>
  <!-- Full screen backdrop when dropdown is open -->
  {#if showDropdown}
    <button 
      class="dropdown-backdrop" 
      onclick={closeDropdown}
      onkeydown={(e) => e.key === 'Escape' && closeDropdown()}
      aria-label="Close dropdown"
      tabindex="-1"
    ></button>
  {/if}
  
  <!-- Main button group -->
  <div class="button-group">
    <!-- Execute button -->
    <button
      class="execute-btn"
      class:processing={isProcessing}
      onclick={handleProcess}
      disabled={!canProcess || !hasAnyProviderEnabled}
      title={`${buttonLabel} (⌘+Shift+G)`}
      aria-label={buttonLabel}
    >
      <!-- Provider icons (overlapped when multiple) -->
      <div class="provider-icons">
        {#if isProcessing}
          <div class="loading-spinner"></div>
        {:else if activeProviderIcons().length > 0}
          {#each activeProviderIcons() as icon, index}
            <img
              src={icon.src}
              alt={icon.alt}
              class="provider-icon"
              class:overlapped={activeProviderIcons().length > 1 && index > 0}
              class:grammar-provider={icon.type === 'grammar'}
              class:ai-provider={icon.type === 'ai'}
              style="z-index: {activeProviderIcons().length - index}"
            />
          {/each}
        {:else}
          <!-- Default icon when no providers selected -->
          <CircleIcon size={14} />
        {/if}
      </div>
      
      <span class="button-label">{buttonLabel}</span>
    </button>
    
    <!-- Dropdown toggle -->
    <button
      class="dropdown-btn"
      onclick={toggleDropdown}
      disabled={isProcessing}
      title="검사 옵션"
      aria-label="검사 옵션"
      aria-expanded={showDropdown}
    >
      <ChevronDown class={showDropdown ? 'rotated' : ''} size={12} />
    </button>
  </div>
  
  <!-- Dropdown menu -->
  {#if showDropdown}
    <div 
      class="dropdown-menu"
      transition:fade={{ duration: 200, easing: cubicOut }}
    >
      <!-- Grammar check section -->
      <div class="dropdown-section">
        <div class="section-header">
          <div class="section-header-left">
            <span class="section-header-title">맞춤법 검사</span>
            {#if grammarCheckEnabled && currentGrammarProvider()}
              <span class="current-selection">{currentGrammarProvider()?.name}</span>
            {/if}
          </div>
          <label class="toggle-switch-minimal">
            <input 
              type="checkbox" 
              checked={grammarCheckEnabled}
              onchange={toggleGrammarCheck}
              aria-label="맞춤법 검사"
            />
            <span class="toggle-slider-minimal"></span>
          </label>
        </div>
        
        {#if grammarCheckEnabled && availableGrammarProviders().length > 0}
          <div 
            class="provider-list"
            transition:slide={{ duration: 200, easing: cubicOut }}
          >
            {#each availableGrammarProviders() as provider}
              <button
                class="provider-item"
                class:selected={currentGrammarProvider()?.id === provider.id}
                onclick={() => selectGrammarProvider(provider.id)}
              >
                {#if provider.icon}
                  <img src={provider.icon} alt={provider.name} class="item-icon" />
                {/if}
                <div class="item-info">
                  <div class="item-name">{provider.name}</div>
                  <div class="item-description">{provider.description}</div>
                </div>
                {#if currentGrammarProvider()?.id === provider.id}
                  <div class="check-icon" in:fade={{ duration: 150 }}>
                    <Check size={14} />
                  </div>
                {/if}
              </button>
            {/each}
          </div>
        {/if}
      </div>
      
      <!-- AI analysis section -->
      <div class="dropdown-section">
        <div class="section-header">
          <div class="section-header-left">
            <span class="section-header-title">AI 분석</span>
            {#if aiAnalysisEnabled && currentAIModel}
              <span class="current-selection">{currentAIModel.name}</span>
            {/if}
          </div>
          <label class="toggle-switch-minimal">
            <input 
              type="checkbox" 
              checked={aiAnalysisEnabled}
              onchange={toggleAIAnalysis}
              aria-label="AI 분석"
            />
            <span class="toggle-slider-minimal"></span>
          </label>
        </div>
        
        {#if aiAnalysisEnabled && availableAIModels().length > 0}
          <div 
            class="model-list"
            transition:slide={{ duration: 200, easing: cubicOut }}
          >
            {#each sortedAIModels() as model}
              <button
                class="provider-item"
                class:selected={currentModelId === model.id}
                onclick={() => selectAIModel(model)}
              >
                <div class="item-info">
                  <div class="item-name">
                    {model.name}
                    {#if model.recommended}
                      <span class="recommended-badge">추천</span>
                    {/if}
                  </div>
                  <div class="item-description">{model.provider}</div>
                </div>
                {#if currentModelId === model.id}
                  <div class="check-icon" in:fade={{ duration: 150 }}>
                    <Check size={14} />
                  </div>
                {/if}
              </button>
            {/each}
          </div>
        {/if}
      </div>
      
    </div>
  {/if}
</div>

<style>
  .unified-check-container {
    position: relative;
  }
  
  .dropdown-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    background: transparent;
    -webkit-app-region: no-drag;
  }
  
  /* Button group */
  .button-group {
    display: flex;
    align-items: center;
    height: 26px;
    border-radius: 5px;
    overflow: hidden;
    background: var(--btn-primary-bg, #3b82f6);
    color: white;
  }
  
  .execute-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 11px;
    height: 100%;
    border: none;
    background: transparent;
    color: white;
    font-size: 11px;
    font-weight: 450;
    letter-spacing: -0.01em;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    transform: translateZ(0); /* Hardware acceleration */
    white-space: nowrap;
  }
  
  .execute-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
  }
  
  .execute-btn:active:not(:disabled) {
    transform: translateY(0);
  }
  
  .execute-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .execute-btn.processing {
    cursor: wait;
  }
  
  /* Provider icons */
  .provider-icons {
    display: flex;
    align-items: center;
    position: relative;
    height: 14px;
    min-width: 14px;
  }
  
  .provider-icon {
    width: 14px;
    height: 14px;
    object-fit: contain;
    position: relative;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    animation: iconFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @keyframes iconFadeIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .provider-icon.overlapped {
    margin-left: -6px;
    box-shadow: -1px 0 2px rgba(0, 0, 0, 0.1);
  }

  /* Apply white filter to AI icons in main button only (not grammar provider icons) */
  .execute-btn .provider-icon.ai-provider {
    filter: brightness(0) invert(1);
  }
  
  .loading-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .button-label {
    line-height: 1;
  }
  
  /* Dropdown button */
  .dropdown-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px 7px;
    height: 100%;
    border: none;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
    background: transparent;
    color: white;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .dropdown-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
  }
  
  .dropdown-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .dropdown-btn :global(svg) {
    width: 12px;
    height: 12px;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center;
  }
  
  .dropdown-btn :global(svg.rotated) {
    transform: rotate(180deg);
  }
  
  /* Dropdown menu */
  .dropdown-menu {
    position: absolute;
    bottom: calc(100% + 4px);
    right: 0;
    min-width: 280px;
    max-width: 320px;
    background: var(--bg-primary);
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    z-index: 10000;
    overflow: hidden;
    transform-origin: bottom right;
    animation: dropdownSlideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @keyframes dropdownSlideIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(4px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .dropdown-section {
    padding: 8px;
    border-bottom: 1px solid var(--border-secondary);
  }
  
  .dropdown-section:last-of-type {
    border-bottom: none;
  }
  
  /* Section header */
  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    user-select: none;
  }
  
  .section-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
  }
  
  .section-header-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-primary);
  }
  
  .current-selection {
    font-size: 11px;
    color: var(--text-secondary);
    animation: fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-4px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* Minimal toggle switch */
  .toggle-switch-minimal {
    position: relative;
    display: inline-block;
    width: 28px;
    height: 16px;
    flex-shrink: 0;
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .toggle-switch-minimal:hover {
    transform: scale(1.05);
  }
  
  .toggle-switch-minimal input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .toggle-switch-minimal input:focus + .toggle-slider-minimal {
    box-shadow: 0 0 0 2px var(--focus-ring);
  }
  
  .toggle-slider-minimal {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #d1d5db;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 16px;
  }
  
  .toggle-slider-minimal:before {
    position: absolute;
    content: "";
    height: 12px;
    width: 12px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .toggle-switch-minimal input:checked + .toggle-slider-minimal {
    background-color: #3b82f6;
  }
  
  .toggle-switch-minimal input:checked + .toggle-slider-minimal:before {
    transform: translateX(12px);
  }
  
  :global(.dark-theme) .toggle-slider-minimal {
    background-color: #4b5563;
  }
  
  :global(.dark-theme) .toggle-switch-minimal input:checked + .toggle-slider-minimal {
    background-color: #3b82f6;
  }
  
  /* Mode toggles */
  /* Provider list */
  .provider-list,
  .model-list {
    padding: 0 8px 8px;
    max-height: 200px;
    overflow-y: auto;
  }
  
  /* Hide scrollbar completely */
  .provider-list::-webkit-scrollbar,
  .model-list::-webkit-scrollbar {
    display: none;
  }
  
  .provider-list,
  .model-list {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
  
  /* Provider items */
  .provider-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 8px;
    border: none;
    background: transparent;
    border-radius: 4px;
    font-size: 12px;
    color: var(--text-primary);
    cursor: pointer;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    text-align: left;
  }
  
  .provider-item:hover {
    background: var(--bg-hover);
    transform: translateX(2px);
  }
  
  .provider-item.selected {
    background: var(--bg-secondary);
    animation: selectPulse 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  @keyframes selectPulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.02);
    }
    100% {
      transform: scale(1);
    }
  }
  
  .item-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
    flex-shrink: 0;
  }
  
  .item-info {
    flex: 1;
    min-width: 0;
  }
  
  .item-name {
    font-weight: 500;
    line-height: 1.3;
  }
  
  .item-description {
    font-size: 10px;
    color: var(--text-secondary);
    line-height: 1.3;
  }
  
  .recommended-badge {
    display: inline-block;
    padding: 1px 4px;
    margin-left: 4px;
    background: var(--success);
    color: white;
    font-size: 9px;
    font-weight: 600;
    border-radius: 3px;
    vertical-align: middle;
  }
  
  .check-icon {
    flex-shrink: 0;
    animation: checkIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .check-icon :global(svg) {
    stroke: var(--success);
    stroke-width: 2;
  }
  
  .check-icon :global(svg path) {
    stroke-dasharray: 20;
    stroke-dashoffset: 20;
    animation: checkDraw 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  
  @keyframes checkIn {
    from {
      opacity: 0;
      transform: scale(0.8) rotate(-45deg);
    }
    to {
      opacity: 1;
      transform: scale(1) rotate(0);
    }
  }
  
  @keyframes checkDraw {
    to {
      stroke-dashoffset: 0;
    }
  }
  
  
  /* Dark mode */
  :global(.dark-theme) .dropdown-menu {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  }
</style>